// UP6 (GOALS/GOALS-UPSTREAM-PARITY.html#UP6): the shared contract for session
// notifications — what a client (the Desk, Pocket) tells the person when a
// background session completes, fails, or needs an answer or approval.
//
// One pure fold, `detectSessionNotifications`, decides the events from two
// readings of the thread shell rows every client already subscribes to (the
// orchestration projection over the authenticated socket, `OrchestrationThreadShell`
// plus the environment id). No door and no effect crosses this file. The laws:
//
// - nothing replays history: a row with no prior mark never notifies, and
//   every mark is dropped while a shell is not "live", so the initial snapshot
//   and every reconnect snapshot only seed;
// - the session the person is looking at never notifies;
// - an archived session never notifies;
// - the fold is channel-agnostic: system notification, in-app notice, sound,
//   badge and Pocket's ongoing activity are each opt-in at the client.
//
// Re-implemented behind Cleo's names from upstream references #11481,
// #11570, #11569, #10416 (t3code, MIT).
import * as Schema from "effect/Schema";
import { sessionInTrouble, type OrchestrationSessionErrorReason } from "./orchestration.ts";

export const SessionNotificationKind = Schema.Literals([
  "completed",
  "failed",
  "input",
  "approval",
]);
export type SessionNotificationKind = typeof SessionNotificationKind.Type;

/** One event: a later event on the same session carries the same `tag`, replacing the earlier notice. */
export const SessionNotificationEvent = Schema.Struct({
  kind: SessionNotificationKind,
  environmentId: Schema.String,
  threadId: Schema.String,
  title: Schema.String,
  worldKey: Schema.NullOr(Schema.String),
  sessionId: Schema.NullOr(Schema.String),
  /** `${environmentId}:${threadId}` — one notice per session. */
  tag: Schema.String,
});
export type SessionNotificationEvent = typeof SessionNotificationEvent.Type;

/** What the fold remembers per session between two readings. */
export const SessionNotificationMark = Schema.Struct({
  /** `${turnId}:${attention}` while the session waits on the person or failed; null otherwise. */
  attention: Schema.NullOr(Schema.String),
  /** The last completed turn's completedAt in epoch ms; null before any completion. */
  completion: Schema.NullOr(Schema.Number),
});
export type SessionNotificationMark = typeof SessionNotificationMark.Type;

/** The shell's status per environment; anything but "live" is a seed, never an event. */
export const SessionNotificationShellStatus = Schema.Literals([
  "empty",
  "cached",
  "synchronizing",
  "live",
]);
export type SessionNotificationShellStatus = typeof SessionNotificationShellStatus.Type;

/**
 * The fields the fold reads from a thread shell row. `OrchestrationThreadShell`
 * with its environment id (the client runtime's `EnvironmentThreadShell`)
 * satisfies this structurally; Pocket's own row shape only has to carry these.
 */
export interface SessionNotificationRow {
  readonly environmentId: string;
  readonly id: string;
  readonly title: string;
  readonly archivedAt: string | null;
  readonly hasPendingApprovals: boolean;
  readonly hasPendingUserInput: boolean;
  readonly worldKey?: string | null | undefined;
  readonly sessionId?: string | null | undefined;
  readonly session: {
    readonly status: string;
    readonly lastErrorReason?: OrchestrationSessionErrorReason | null | undefined;
  } | null;
  readonly latestTurn: {
    readonly turnId: string;
    readonly state: string;
    readonly completedAt: string | null;
  } | null;
}

export const sessionNotificationKey = (row: {
  readonly environmentId: string;
  readonly id: string;
}): string => `${row.environmentId}:${row.id}`;

type AttentionState = "input" | "approval" | "failed" | null;

function attentionOf(row: SessionNotificationRow): AttentionState {
  if (row.hasPendingApprovals) return "approval";
  if (row.hasPendingUserInput) return "input";
  // W-0b60a0cc: an error that is a notice (a missing folder refused the Send)
  // is not a failed session to announce.
  if (row.latestTurn?.state === "error" || sessionInTrouble(row.session)) return "failed";
  return null;
}

export function markOf(row: SessionNotificationRow): SessionNotificationMark {
  const attention = attentionOf(row);
  const turnId = row.latestTurn?.turnId ?? "none";
  const completedAt =
    row.latestTurn?.state === "completed" && row.latestTurn.completedAt !== null
      ? Date.parse(row.latestTurn.completedAt)
      : null;
  return {
    attention: attention === null ? null : `${turnId}:${attention}`,
    completion: completedAt !== null && Number.isFinite(completedAt) ? completedAt : null,
  };
}

export interface DetectSessionNotificationsInput<Row extends SessionNotificationRow> {
  readonly threads: ReadonlyArray<Row>;
  readonly marks: ReadonlyMap<string, SessionNotificationMark>;
  /** Shell status per environment id; anything but "live" is a seed, never an event. */
  readonly shellStatus: (environmentId: string) => SessionNotificationShellStatus;
  /** Whether the person has this session open right now. */
  readonly isActive: (row: Row) => boolean;
}

export interface DetectSessionNotificationsResult {
  readonly events: ReadonlyArray<SessionNotificationEvent>;
  readonly marks: ReadonlyMap<string, SessionNotificationMark>;
}

/**
 * One reading of the shell. Returns the events to raise and the marks to
 * remember for the next reading. Pure: call it with the previous result's
 * marks.
 */
export function detectSessionNotifications<Row extends SessionNotificationRow>(
  input: DetectSessionNotificationsInput<Row>,
): DetectSessionNotificationsResult {
  const next = new Map<string, SessionNotificationMark>();
  const events: Array<SessionNotificationEvent> = [];
  for (const row of input.threads) {
    const key = sessionNotificationKey(row);
    const mark = markOf(row);
    const live = input.shellStatus(row.environmentId) === "live";
    if (!live) continue; // a synchronizing or cached shell only seeds once it is live
    // Carry the last completion forward when the latest turn is not completed,
    // so an interrupted or running turn never makes an old completion "new".
    const prior = input.marks.get(key);
    const carried: SessionNotificationMark = {
      attention: mark.attention,
      completion: mark.completion ?? prior?.completion ?? null,
    };
    next.set(key, carried);
    if (prior === undefined) continue; // first sight: seed, never replay
    if (row.archivedAt !== null) continue;
    if (input.isActive(row)) continue;
    const kind: SessionNotificationKind | null =
      carried.attention !== null && carried.attention !== prior.attention
        ? (carried.attention.split(":").at(-1) as "input" | "approval" | "failed")
        : carried.completion !== null &&
            (prior.completion === null || carried.completion > prior.completion)
          ? "completed"
          : null;
    if (kind === null) continue;
    events.push({
      kind,
      environmentId: row.environmentId,
      threadId: row.id,
      title: row.title,
      worldKey: row.worldKey ?? null,
      sessionId: row.sessionId ?? null,
      tag: key,
    });
  }
  return { events, marks: next };
}
